import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { tap } from 'rxjs';
import {
  type CreateRole,
  type RoleDetailsResponse,
  type RolePermissionsResponse,
  type RolesResponse,
  type RoleUsersResponse,
  type UpdateRole,
} from '@zag/auth/models';
import type { PermissionScope } from './permission-checklist/permission-scope-order';
import { PermissionService } from './permission.service';
import { SearchService } from '@zag/ui';

// Re-export shared types for convenience
export type {
  CreateRole,
  ManagedRole,
  RoleDetails,
  RolePermission,
  RoleUserInherited,
  RoleUserSummary,
  RoleUsersResponse,
  UpdateRole,
} from '@zag/auth/models';

/** One scope-carrying role-permission grant for `updatePermissionsWithScopes`. */
export interface RolePermissionScopeGrant {
  permissionId: string;
  scope: PermissionScope;
}

@Injectable({ providedIn: 'root' })
export class RolesManagementService {
  private readonly http = inject(HttpClient);
  private readonly searchService = inject(SearchService);
  private readonly permissionService = inject(PermissionService);
  private readonly basePath = '/api/v1/rbac/roles';

  private readonly refreshTrigger = signal(0);
  readonly selectedId = signal<string | undefined>(undefined);

  private readonly listResource = httpResource<RolesResponse>(() => {
    this.refreshTrigger();
    if (!this.permissionService.hasPermission('rbac:roles:read')) {
      return undefined;
    }
    // Track catalog search query to trigger reload when it changes
    const searchQuery = this.searchService.catalogSearchQuery();

    // Build URL with search parameter if present
    if (searchQuery) {
      return `${this.basePath}?search=${encodeURIComponent(searchQuery)}`;
    }
    return this.basePath;
  });

  private readonly selectedResource = httpResource<RoleDetailsResponse>(() => {
    this.refreshTrigger();
    const id = this.selectedId();
    return id ? `${this.basePath}/${id}` : undefined;
  });

  private readonly usersRefreshTrigger = signal(0);

  private readonly usersResource = httpResource<RoleUsersResponse>(() => {
    const id = this.selectedId();
    this.usersRefreshTrigger();
    return id ? `${this.basePath}/${id}/users` : undefined;
  });

  readonly roles = computed(() => this.listResource.value() ?? []);
  readonly isLoading = () => this.listResource.isLoading();
  readonly error = () => this.listResource.error();

  readonly selectedRole = computed(() => this.selectedResource.value());
  readonly isLoadingSelected = () => this.selectedResource.isLoading();

  readonly assignedUsers = computed<RoleUsersResponse>(() => this.usersResource.value() ?? { direct: [], inherited: [] });
  readonly isLoadingAssignedUsers = () => this.usersResource.isLoading();
  readonly assignedUsersError = () => this.usersResource.error();

  reloadAssignedUsers(): void {
    this.usersRefreshTrigger.update(v => v + 1);
  }

  reload(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  selectById(id: string): void {
    this.selectedId.set(id);
  }

  clearSelection(): void {
    this.selectedId.set(undefined);
  }

  create(data: CreateRole) {
    return this.http.post<RoleDetailsResponse>(this.basePath, data);
  }

  update(id: string, data: UpdateRole) {
    return this.http.patch<RoleDetailsResponse>(`${this.basePath}/${id}`, data);
  }

  delete(id: string) {
    return this.http.delete<void>(`${this.basePath}/${id}`).pipe(
      tap(() => {
        this.reload();
      })
    );
  }

  getPermissions(id: string) {
    return this.http.get<RolePermissionsResponse>(`${this.basePath}/${id}/permissions`);
  }

  updatePermissions(id: string, permissionIds: string[]) {
    return this.http.put<RolePermissionsResponse>(`${this.basePath}/${id}/permissions`, { permissionIds });
  }

  // Scope-carrying save for the tri-state role checklist; the WP-4 server accepts `{ permissions }`.
  updatePermissionsWithScopes(id: string, grants: RolePermissionScopeGrant[]) {
    return this.http.put<RolePermissionsResponse>(`${this.basePath}/${id}/permissions`, { permissions: grants });
  }

  /** Assign a role to a user. Idempotent — repeat calls do not create duplicates. */
  assignUserToRole(roleId: string, userId: string) {
    return this.http.post<void>(`${this.basePath}/${roleId}/users/${userId}`, {});
  }

  /**
   * Remove a directly-assigned role from a user. Group-derived assignments
   * are untouched.
   */
  unassignUserFromRole(roleId: string, userId: string) {
    return this.http.delete<void>(`${this.basePath}/${roleId}/users/${userId}`);
  }
}
